import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import {
  downloadOrderItemWithMargin,
  exportOrderItemsZip,
  fetchPaidOrderItems,
  updateAdminOrderItemStatus,
  type AdminOrderExportDpi,
  type AdminOrderExportGroupInput,
  type AdminOrderItem,
  type AdminOrderItemStatus,
} from '@/services/adminService';
import { analytics } from '@/services/analytics';
import type { MeasurementUnit } from '@/types/measurement';
import { ORDER_ITEM_STATUSES } from '@/types/orderStatus';
import i18n from '@/i18n';
import { logging } from '@/services/logging';
import {
  PAID_UI_ITEM_MEMBER_STATUSES,
  type AdminWorkspaceItemGroup,
  type AdminWorkspaceItemGroupMode,
} from './types';
import {
  getDefaultConfirmedDateRange,
  isTimestampWithinDateRange,
  matchesSearchTerms,
  normalizeSearchInput,
  formatOrderItemReadableCode,
} from './utils';

const getMaxDimension = (item: AdminOrderItem): number => Math.max(item.widthIn ?? 0, item.heightIn ?? 0);
const buildGroupKey = (item: AdminOrderItem): string => `${item.paperType?.id ?? 'paper:unknown'}::${item.printer?.id ?? 'printer:unknown'}`;
const buildOrderGroupKey = (item: AdminOrderItem): string => item.orderId ? 'order:' + item.orderId : 'order:unassigned';

const ADMIN_ORDER_ITEMS_FILTERS_STORAGE_KEY = 'tulasi.adminOrdersWorkspace.itemFilters';
const LEGACY_ITEM_STATUS_FILTER_OPTIONS: ReadonlyArray<AdminOrderItemStatus> = [
  'FIRST_ORDER',
  'SECOND_ORDER',
  'PAID',
  'REUPLOAD_IMAGE',
  'REUPLOADED',
  'READY_TO_PRINT',
  'PRINTED',
];

const EXPORTABLE_ITEM_STATUSES: ReadonlySet<AdminOrderItemStatus> = new Set([
  'FIRST_ORDER',
  'SECOND_ORDER',
  'PAID',
  'REUPLOAD_IMAGE',
  'REUPLOADED',
  'READY_TO_PRINT',
]);

type StoredAdminOrderItemsFilters = {
  readonly groupMode: AdminWorkspaceItemGroupMode;
  readonly searchInput: string;
  readonly dateFrom: string;
  readonly dateTo: string;
  readonly onlyFirstOrder: boolean;
  readonly statusFilters: AdminOrderItemStatus[];
  readonly exportDpiByGroupKey: Record<string, AdminOrderExportDpi>;
};

const normalizeStoredItemStatusFilters = (statusFilters: ReadonlyArray<AdminOrderItemStatus>): AdminOrderItemStatus[] => {
  if (statusFilters.length === 0) {
    return [...ORDER_ITEM_STATUSES];
  }

  const normalizedStatuses = new Set(statusFilters);
  if (PAID_UI_ITEM_MEMBER_STATUSES.some((statusValue) => normalizedStatuses.has(statusValue))) {
    PAID_UI_ITEM_MEMBER_STATUSES.forEach((statusValue) => normalizedStatuses.add(statusValue));
  }

  return ORDER_ITEM_STATUSES.filter((statusValue) => normalizedStatuses.has(statusValue));
};

const readStoredAdminOrderItemsFilters = (defaults: { readonly dateFrom: string; readonly dateTo: string }): StoredAdminOrderItemsFilters => {
  const defaultStatusFilters = [...ORDER_ITEM_STATUSES];
  if (typeof window === 'undefined') {
    return { groupMode: 'paperType', searchInput: '', dateFrom: defaults.dateFrom, dateTo: defaults.dateTo, onlyFirstOrder: false, statusFilters: defaultStatusFilters, exportDpiByGroupKey: {} };
  }

  try {
    const stored = window.localStorage.getItem(ADMIN_ORDER_ITEMS_FILTERS_STORAGE_KEY);
    if (!stored) {
      return { groupMode: 'paperType', searchInput: '', dateFrom: defaults.dateFrom, dateTo: defaults.dateTo, onlyFirstOrder: false, statusFilters: defaultStatusFilters, exportDpiByGroupKey: {} };
    }

    const parsed = JSON.parse(stored) as Partial<StoredAdminOrderItemsFilters>;
    const parsedStatusFilters = Array.isArray(parsed.statusFilters)
      ? normalizeStoredItemStatusFilters(parsed.statusFilters.filter((value): value is AdminOrderItemStatus => ORDER_ITEM_STATUSES.includes(value as AdminOrderItemStatus)))
      : defaultStatusFilters;
    const shouldUpgradeLegacyFilters = parsedStatusFilters.length === LEGACY_ITEM_STATUS_FILTER_OPTIONS.length
      && LEGACY_ITEM_STATUS_FILTER_OPTIONS.every((statusValue) => parsedStatusFilters.includes(statusValue));

    return {
      groupMode: parsed.groupMode === 'order' ? 'order' : 'paperType',
      searchInput: typeof parsed.searchInput === 'string' ? parsed.searchInput : '',
      dateFrom: typeof parsed.dateFrom === 'string' ? parsed.dateFrom : defaults.dateFrom,
      dateTo: typeof parsed.dateTo === 'string' ? parsed.dateTo : defaults.dateTo,
      onlyFirstOrder: parsed.onlyFirstOrder === true,
      statusFilters: parsedStatusFilters.length === 0 || shouldUpgradeLegacyFilters ? defaultStatusFilters : parsedStatusFilters,
      exportDpiByGroupKey: parsed.exportDpiByGroupKey != null && typeof parsed.exportDpiByGroupKey === 'object' ? parsed.exportDpiByGroupKey as Record<string, AdminOrderExportDpi> : {},
    };
  } catch {
    return { groupMode: 'paperType', searchInput: '', dateFrom: defaults.dateFrom, dateTo: defaults.dateTo, onlyFirstOrder: false, statusFilters: defaultStatusFilters, exportDpiByGroupKey: {} };
  }
};

export const useAdminOrderItemsData = (enabled: boolean, onAfterMutation?: () => void, measurementUnit: MeasurementUnit = 'in') => {
  const { toast } = useToast();
  const defaultDateRange = useMemo(() => ({ ...getDefaultConfirmedDateRange(), dateTo: '' }), []);
  const storedFilters = useMemo(() => readStoredAdminOrderItemsFilters(defaultDateRange), [defaultDateRange]);
  const [items, setItems] = useState<AdminOrderItem[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());
  const [exportingGroupKeys, setExportingGroupKeys] = useState<Set<string>>(new Set());
  const [exportDpiByGroupKey, setExportDpiByGroupKey] = useState<Record<string, AdminOrderExportDpi>>(storedFilters.exportDpiByGroupKey);
  const [isExportingAll, setIsExportingAll] = useState(false);
  const [downloadingWithMarginIds, setDownloadingWithMarginIds] = useState<Set<string>>(new Set());
  const [groupMode, setGroupMode] = useState<AdminWorkspaceItemGroupMode>(storedFilters.groupMode);
  const [searchInput, setSearchInput] = useState(storedFilters.searchInput);
  const [dateFrom, setDateFrom] = useState(storedFilters.dateFrom);
  const [dateTo, setDateTo] = useState(storedFilters.dateTo);
  const [debouncedDateFrom, setDebouncedDateFrom] = useState(storedFilters.dateFrom);
  const [debouncedDateTo, setDebouncedDateTo] = useState(storedFilters.dateTo);
  const [onlyFirstOrder, setOnlyFirstOrder] = useState(storedFilters.onlyFirstOrder);
  const [statusFilters, setStatusFilters] = useState<Set<AdminOrderItemStatus>>(() => new Set(storedFilters.statusFilters.length > 0 ? storedFilters.statusFilters : ORDER_ITEM_STATUSES));
  const [reuploadDialogItem, setReuploadDialogItem] = useState<AdminOrderItem | null>(null);
  const [reuploadComment, setReuploadComment] = useState('');
  const [reuploadSubmitting, setReuploadSubmitting] = useState(false);

  const loadItems = useCallback(async () => {
    try {
      setStatus('loading');
      setError(null);
      const result = await fetchPaidOrderItems({ dateFrom: debouncedDateFrom, dateTo: debouncedDateTo });
      setItems([...result]);
      setStatus('idle');
    } catch (err) {
      logging.error('Failed to fetch order items', { error: err });
      const message = i18n.t('adminOrdersWorkspace.toasts.loadItemsDescription', 'Failed to load order items.');
      setStatus('error');
      setError(message);
      toast({
        title: i18n.t('adminOrdersWorkspace.toasts.loadItemsTitle', 'Failed to load order items'),
        description: message,
        variant: 'destructive',
      });
    }
  }, [debouncedDateFrom, debouncedDateTo, measurementUnit, toast]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    void loadItems();
  }, [enabled, loadItems]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedDateFrom(dateFrom);
      setDebouncedDateTo(dateTo);
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(ADMIN_ORDER_ITEMS_FILTERS_STORAGE_KEY, JSON.stringify({
      groupMode,
      searchInput,
      dateFrom,
      dateTo,
      onlyFirstOrder,
      statusFilters: Array.from(statusFilters),
      exportDpiByGroupKey,
    } satisfies StoredAdminOrderItemsFilters));
  }, [dateFrom, dateTo, exportDpiByGroupKey, groupMode, onlyFirstOrder, searchInput, statusFilters]);

  const searchTerms = useMemo(
    () => normalizeSearchInput(searchInput).split(' ').filter((term) => term.length > 0),
    [searchInput],
  );

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (!statusFilters.has(item.status)) {
        return false;
      }

      if (!isTimestampWithinDateRange(item.order?.confirmedAt ?? item.order?.createdAt ?? item.createdAt, dateFrom, dateTo)) {
        return false;
      }

      if (onlyFirstOrder && item.status !== 'FIRST_ORDER') {
        return false;
      }

      return matchesSearchTerms(searchTerms, [
        item.order?.displayCode ?? null,
        item.orderId,
        item.id,
        formatOrderItemReadableCode(item.id),
        item.customer?.name ?? null,
        item.customer?.email ?? null,
        item.shippingAddress?.firstName ?? null,
        item.shippingAddress?.lastName ?? null,
        item.shippingAddress?.phone ?? null,
        item.shippingAddress?.street1 ?? null,
        item.shippingAddress?.street2 ?? null,
        item.shippingAddress?.city ?? null,
        item.shippingAddress?.region ?? null,
        item.shippingAddress?.postalCode ?? null,
        item.shippingAddress?.countryCode ?? null,
        item.uploadedFile?.filename ?? null,
      ]);
    });
  }, [dateFrom, dateTo, items, onlyFirstOrder, searchTerms, statusFilters]);

  const groupedItems = useMemo<AdminWorkspaceItemGroup[]>(() => {
    const grouped = new Map<string, AdminWorkspaceItemGroup>();
    for (const item of filteredItems) {
      const key = groupMode === 'paperType' ? buildGroupKey(item) : buildOrderGroupKey(item);
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          paper: groupMode === 'paperType'
            ? item.paperType ? `${item.paperType.name} (${item.paperType.brand})` : i18n.t('adminOrdersWorkspace.grouping.unknownPaper', 'Unknown paper')
            : item.order?.displayCode
              ? i18n.t('adminOrdersWorkspace.grouping.orderLabel', 'Order {{displayCode}}', { displayCode: item.order.displayCode })
              : i18n.t('adminOrdersWorkspace.grouping.unassignedItems', 'Unassigned items'),
          printer: groupMode === 'paperType'
            ? item.printer ? `${item.printer.name} (${item.printer.model})` : i18n.t('adminOrdersWorkspace.grouping.unknownPrinter', 'Unknown printer')
            : item.orderId
              ? (item.customer?.email ?? item.customer?.name ?? i18n.t('adminOrdersWorkspace.common.unknownCustomer', 'Unknown customer'))
              : i18n.t('adminOrdersWorkspace.grouping.noOrderAssigned', 'No order assigned'),
          items: [],
        });
      }
      grouped.get(key)?.items.push(item);
    }

    const groups = Array.from(grouped.values());
    for (const group of groups) {
      group.items.sort((a, b) => {
        if (groupMode === 'order') {
          const paperSort = (a.paperType?.name ?? '').localeCompare(b.paperType?.name ?? '');
          if (paperSort !== 0) {
            return paperSort;
          }
        }
        return getMaxDimension(b) - getMaxDimension(a);
      });
    }

    return groups.sort((a, b) => a.paper.localeCompare(b.paper) || a.printer.localeCompare(b.printer));
  }, [filteredItems, groupMode]);

  const toggleStatusFilter = useCallback((targetStatus: AdminOrderItemStatus) => {
    setStatusFilters((prev) => {
      const next = new Set(prev);
      if (next.has(targetStatus)) {
        next.delete(targetStatus);
      } else {
        next.add(targetStatus);
      }
      return next;
    });
  }, []);

  const markUpdating = useCallback((orderItemId: string, updating: boolean) => {
    setUpdatingIds((prev) => {
      const next = new Set(prev);
      if (updating) {
        next.add(orderItemId);
      } else {
        next.delete(orderItemId);
      }
      return next;
    });
  }, []);

  const resetFilters = useCallback(() => {
    setGroupMode('paperType');
    setSearchInput('');
    setDateFrom(defaultDateRange.dateFrom);
    setDateTo(defaultDateRange.dateTo);
    setDebouncedDateFrom(defaultDateRange.dateFrom);
    setDebouncedDateTo(defaultDateRange.dateTo);
    setOnlyFirstOrder(false);
    setStatusFilters(new Set(ORDER_ITEM_STATUSES));
    setExportDpiByGroupKey({});
  }, [defaultDateRange.dateFrom, defaultDateRange.dateTo]);

  const handleItemStatusChange = useCallback(async (orderItemId: string, nextStatus: AdminOrderItemStatus) => {
    if (!ORDER_ITEM_STATUSES.includes(nextStatus)) {
      return;
    }

    if (nextStatus === 'REUPLOAD_IMAGE') {
      const item = items.find((entry) => entry.id === orderItemId) ?? null;
      if (!item) {
        return;
      }
      setReuploadDialogItem(item);
      setReuploadComment('');
      return;
    }

    markUpdating(orderItemId, true);
    setError(null);
    try {
      const updated = await updateAdminOrderItemStatus(orderItemId, nextStatus);
      setItems((prev) => prev.map((item) => item.id === updated.id ? { ...item, status: updated.status, updatedAt: updated.updatedAt } : item));
      await loadItems();
      onAfterMutation?.();
    } catch (err) {
      logging.error('Failed to update order item status', { error: err, orderItemId });
      const message = err instanceof Error ? err.message : i18n.t('adminOrdersWorkspace.toasts.statusUpdateDescription', 'Failed to update order item status.');
      setError(message);
      toast({
        title: i18n.t('adminOrdersWorkspace.toasts.statusUpdateTitle', 'Status update failed'),
        description: message,
        variant: 'destructive',
      });
    } finally {
      markUpdating(orderItemId, false);
    }
  }, [items, loadItems, markUpdating, onAfterMutation, toast]);


  const closeReuploadDialog = useCallback(() => {
    if (reuploadSubmitting) {
      return;
    }
    setReuploadDialogItem(null);
    setReuploadComment('');
  }, [reuploadSubmitting]);

  const confirmReuploadDialog = useCallback(async () => {
    const item = reuploadDialogItem;
    const comment = reuploadComment.trim();
    if (!item || comment.length === 0) {
      return;
    }

    markUpdating(item.id, true);
    setReuploadSubmitting(true);
    setError(null);
    try {
      const updated = await updateAdminOrderItemStatus(item.id, 'REUPLOAD_IMAGE', comment);
      setItems((prev) => prev.map((entry) => entry.id === updated.id ? { ...entry, status: updated.status, updatedAt: updated.updatedAt } : entry));
      setReuploadDialogItem(null);
      setReuploadComment('');
      await loadItems();
      onAfterMutation?.();
      toast({ title: i18n.t('adminOrdersWorkspace.toasts.reuploadRequestSent', 'Reupload request sent') });
    } catch (err) {
      logging.error('Failed to submit reupload request', { error: err, orderItemId: item.id });
      const message = err instanceof Error ? err.message : i18n.t('adminOrdersWorkspace.toasts.reuploadRequestDescription', 'Failed to submit reupload request.');
      setError(message);
      toast({
        title: i18n.t('adminOrdersWorkspace.toasts.reuploadRequestTitle', 'Reupload request failed'),
        description: message,
        variant: 'destructive',
      });
    } finally {
      setReuploadSubmitting(false);
      markUpdating(item.id, false);
    }
  }, [loadItems, markUpdating, onAfterMutation, reuploadComment, reuploadDialogItem, toast]);

  const downloadExport = useCallback((blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }, []);

  const exportItems = useCallback(async (targetItems: AdminOrderItem[], targetDpi: AdminOrderExportDpi = 'AUTO') => {
    const eligibleItems = targetItems.filter((item) => EXPORTABLE_ITEM_STATUSES.has(item.status));
    if (eligibleItems.length === 0) {
      toast({
        title: i18n.t('adminOrdersWorkspace.toasts.noEligibleItemsTitle', 'No eligible items'),
        description: i18n.t('adminOrdersWorkspace.toasts.noEligibleItemsDescription', 'Only items in 1st Order, 2nd Order, Paid, Reupload Image, Reuploaded, or Ready to Print can be exported.'),
      });
      return;
    }
    const result = await exportOrderItemsZip(eligibleItems.map((item) => item.id), targetDpi, measurementUnit);
    downloadExport(result.blob, result.filename);
    await loadItems();
    onAfterMutation?.();
  }, [downloadExport, loadItems, measurementUnit, onAfterMutation, toast]);

  const setGroupExportDpi = useCallback((groupKey: string, targetDpi: AdminOrderExportDpi) => {
    setExportDpiByGroupKey((current) => ({ ...current, [groupKey]: targetDpi }));
  }, []);

  const handleExportAll = useCallback(async () => {
    setError(null);
    setIsExportingAll(true);
    try {
      const eligibleCount = filteredItems.filter((item) => EXPORTABLE_ITEM_STATUSES.has(item.status)).length;
      const exportGroups: AdminOrderExportGroupInput[] = groupedItems
        .map((group) => ({
          itemIds: group.items.filter((item) => EXPORTABLE_ITEM_STATUSES.has(item.status)).map((item) => item.id),
          targetDpi: exportDpiByGroupKey[group.key] ?? 'AUTO',
        }))
        .filter((group) => group.itemIds.length > 0);
      analytics.track('button: ui.admin_print_orders_export_all', { itemCount: filteredItems.length, eligibleCount, groupCount: exportGroups.length });
      const result = await exportOrderItemsZip(
        exportGroups.flatMap((group) => group.itemIds),
        'AUTO',
        measurementUnit,
        exportGroups,
      );
      downloadExport(result.blob, result.filename);
      await loadItems();
      onAfterMutation?.();
    } catch (err) {
      logging.error('Failed to export print order items', { error: err });
      const message = err instanceof Error ? err.message : i18n.t('adminOrdersWorkspace.toasts.exportItemsDescription', 'Failed to export print order items.');
      setError(message);
      toast({
        title: i18n.t('adminOrdersWorkspace.toasts.exportFailedTitle', 'Export failed'),
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsExportingAll(false);
    }
  }, [downloadExport, exportDpiByGroupKey, filteredItems, groupedItems, loadItems, measurementUnit, onAfterMutation, toast]);

  const handleExportGroup = useCallback(async (groupKey: string, groupItems: AdminOrderItem[], targetDpi: AdminOrderExportDpi = 'AUTO') => {
    setError(null);
    setExportingGroupKeys((prev) => new Set(prev).add(groupKey));
    try {
      const eligibleCount = groupItems.filter((item) => EXPORTABLE_ITEM_STATUSES.has(item.status)).length;
      const resolvedTargetDpi = exportDpiByGroupKey[groupKey] ?? targetDpi;
      analytics.track('button: ui.admin_print_orders_export_group', { groupKey, itemCount: groupItems.length, eligibleCount, targetDpi: resolvedTargetDpi });
      await exportItems(groupItems, resolvedTargetDpi);
    } catch (err) {
      logging.error('Failed to export group items', { error: err, groupKey });
      const message = err instanceof Error ? err.message : i18n.t('adminOrdersWorkspace.toasts.exportGroupDescription', 'Failed to export group items.');
      setError(message);
      toast({
        title: i18n.t('adminOrdersWorkspace.toasts.exportFailedTitle', 'Export failed'),
        description: message,
        variant: 'destructive',
      });
    } finally {
      setExportingGroupKeys((prev) => {
        const next = new Set(prev);
        next.delete(groupKey);
        return next;
      });
    }
  }, [exportDpiByGroupKey, exportItems, toast]);

  const handleDownloadWithMargin = useCallback(async (item: AdminOrderItem) => {
    setDownloadingWithMarginIds((prev) => new Set(prev).add(item.id));
    setError(null);
    try {
      analytics.track('button: ui.admin_print_orders_download_with_margin_file', {
        orderItemId: item.id,
        fileId: item.uploadedFile?.id ?? null,
      });
      const selectedTargetDpi = exportDpiByGroupKey[buildGroupKey(item)] ?? 'AUTO';
      const result = await downloadOrderItemWithMargin(item.id, measurementUnit, selectedTargetDpi);
      setItems((prev) => prev.map((entry) => entry.id === result.item.id ? { ...entry, status: result.item.status, updatedAt: result.item.updatedAt } : entry));
      onAfterMutation?.();

      const link = document.createElement('a');
      link.href = result.downloadUrl;
      link.download = result.filename;
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      logging.error('Failed to download order item file with margin', { error: err, orderItemId: item.id });
      const message = err instanceof Error ? err.message : i18n.t('adminOrdersWorkspace.toasts.downloadWithMarginDescription', 'Failed to download file with margin.');
      setError(message);
      toast({
        title: i18n.t('adminOrdersWorkspace.toasts.downloadFailedTitle', 'Download failed'),
        description: message,
        variant: 'destructive',
      });
    } finally {
      setDownloadingWithMarginIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  }, [exportDpiByGroupKey, measurementUnit, onAfterMutation, toast]);

  return {
    state: {
      status,
      error,
      items,
      filteredItems,
      groupedItems,
      groupMode,
      searchInput,
      dateFrom,
      dateTo,
      onlyFirstOrder,
      statusFilters,
      updatingIds,
      exportingGroupKeys,
      exportDpiByGroupKey,
      isExportingAll,
      downloadingWithMarginIds,
      reuploadDialogItem,
      reuploadComment,
      reuploadSubmitting,
    },
    actions: {
      loadItems,
      setGroupMode,
      setSearchInput,
      setDateFrom,
      setDateTo,
      setOnlyFirstOrder,
      resetFilters,
      toggleStatusFilter,
      setGroupExportDpi,
      handleItemStatusChange,
      handleExportAll,
      handleExportGroup,
      handleDownloadWithMargin,
      setReuploadComment,
      closeReuploadDialog,
      confirmReuploadDialog,
    },
  };
};
